import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { CdkDragStart, CdkDragEnd } from '@angular/cdk/drag-drop';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appAutoScroll]'
})
export class AutoScrollDirective implements OnInit, OnDestroy {
  @Input() scrollSpeed: number = 20
  @Input() scrollThreshold: number = 60;
  @Input() scrollContainer: HTMLElement | null = null;

  private scrollInterval: any;
  private currentMouseY: number = 0;
  private isDragging: boolean = false;
  private destroy$ = new Subject<void>();
  private lastScrollTime: number = 0;
  private scrollDelay: number = 4; // Reduced for faster response

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    // Listen for drag start events
    fromEvent<CdkDragStart>(this.elementRef.nativeElement, 'cdkDragStarted')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.startAutoScroll();
      });

    // Listen for drag end events
    fromEvent<CdkDragEnd>(this.elementRef.nativeElement, 'cdkDragEnded')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.stopAutoScroll();
      });
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.isDragging) {
      this.currentMouseY = event.clientY;
      this.handleAutoScroll(); // Immediate response
    }
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (this.isDragging && event.touches.length > 0) {
      this.currentMouseY = event.touches[0].clientY;
      this.handleAutoScroll(); // Immediate response
    }
  }

  private startAutoScroll() {
    this.isDragging = true;
    this.stopAutoScroll(); // Clear any existing interval
    
    // Use requestAnimationFrame for smoother scrolling
    this.scrollInterval = setInterval(() => {
      this.handleAutoScroll();
    }, this.scrollDelay);
  }

  private stopAutoScroll() {
    this.isDragging = false;
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  private handleAutoScroll() {
    if (!this.isDragging) return;

    const now = Date.now();
    if (now - this.lastScrollTime < this.scrollDelay) return; // Throttle for performance

    const container = this.getScrollContainer();
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Calculate distance from container edges
    const distanceFromTop = this.currentMouseY - containerRect.top;
    const distanceFromBottom = containerRect.bottom - this.currentMouseY;

    let shouldScroll = false;
    let scrollDirection = 0;

    // Auto-scroll up when near top edge
    if (distanceFromTop < this.scrollThreshold && distanceFromTop > 0 && scrollTop > 0) {
      shouldScroll = true;
      scrollDirection = -1;
    }

    // Auto-scroll down when near bottom edge
    if (distanceFromBottom < this.scrollThreshold && distanceFromBottom > 0 && scrollTop < (scrollHeight - clientHeight)) {
      shouldScroll = true;
      scrollDirection = 1;
    }

    if (shouldScroll) {
      // Calculate scroll amount based on distance from edge
      const edgeDistance = Math.min(distanceFromTop, distanceFromBottom);
      const scrollMultiplier = Math.max(1, (this.scrollThreshold - edgeDistance) / this.scrollThreshold);
      const scrollAmount = Math.round(this.scrollSpeed * scrollMultiplier);
      
      container.scrollTop += scrollDirection * scrollAmount;
      this.lastScrollTime = now;
    }
  }

  private getScrollContainer(): HTMLElement | null {
    // Use provided container or find the closest scrollable parent
    if (this.scrollContainer) {
      return this.scrollContainer;
    }

    // Find the closest scrollable parent
    let element = this.elementRef.nativeElement;
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return element;
      }
      element = element.parentElement;
    }

    return null;
  }

  ngOnDestroy() {
    this.stopAutoScroll();
    this.destroy$.next();
    this.destroy$.complete();
  }
} 